// thumbnailQueue.ts
// Очередь рендера миниатюр: одна задача за раз, планирование в простое браузера.
// Без React и без внешних импортов — переиспользуется и в pda-cfront.
//
// Почему concurrency 1: растеризация в canvas идёт в main thread (pdf.js уносит
// в свой воркер только парсинг). Две параллельные задачи не ускорят её, а лишь
// удлинят самый долгий кадр. Один рендер за раз + планирование в idle означает,
// что между миниатюрами браузер всегда успевает отдать кадр.

type IdleHandle = number;

interface IdleDeadline {
    didTimeout: boolean;
    timeRemaining(): number;
}

// requestIdleCallback нет в Safari до 17 и в jsdom — фолбэк на макротаск.
// Он не даёт гарантий простоя, но сохраняет главное: работа не идёт синхронно
// подряд, между задачами вклинивается рендер-кадр.
const scheduleIdle: (cb: (deadline: IdleDeadline) => void) => IdleHandle =
    typeof requestIdleCallback === 'function'
        ? (cb) => requestIdleCallback(cb, { timeout: 1000 })
        : (cb) => setTimeout(() => cb({ didTimeout: true, timeRemaining: () => 0 }), 0) as unknown as IdleHandle;

const cancelIdle: (handle: IdleHandle) => void =
    typeof cancelIdleCallback === 'function' ? cancelIdleCallback : (h) => clearTimeout(h as unknown as number);

interface Job {
    key: string;
    priority: number;
    run: () => Promise<void>;
}

export class ThumbnailQueue {
    private pending = new Map<string, Job>();
    private runningKey: string | null = null;
    private idleHandle: IdleHandle | null = null;
    private disposed = false;

    /**
     * Поставить задачу. Повторный push с тем же ключом только обновляет приоритет —
     * так скролл переставляет очередь, не плодя дубликаты. Меньше priority — раньше.
     */
    push(key: string, priority: number, run: () => Promise<void>): void {
        if (this.disposed || this.runningKey === key) return;

        const existing = this.pending.get(key);
        if (existing) {
            existing.priority = priority;
            return;
        }

        this.pending.set(key, { key, priority, run });
        this.schedule();
    }

    /**
     * Выбросить незапущенные задачи, ключей которых нет в keep.
     *
     * Текущая выполняющаяся задача НЕ отменяется намеренно: pdf.js отдаёт странице
     * общий operator list, и cancel() рендера миниатюры обрушил бы его для основного
     * рендера той же страницы. Миниатюра дорисовывается до конца — это единицы
     * миллисекунд, дешевле любой синхронизации.
     */
    retainOnly(keep: Set<string>): void {
        for (const key of [...this.pending.keys()]) {
            if (!keep.has(key)) this.pending.delete(key);
        }
    }

    clear(): void {
        this.pending.clear();
    }

    dispose(): void {
        this.disposed = true;
        this.pending.clear();
        if (this.idleHandle !== null) {
            cancelIdle(this.idleHandle);
            this.idleHandle = null;
        }
    }

    private schedule(): void {
        if (this.disposed || this.idleHandle !== null || this.runningKey !== null) return;
        if (this.pending.size === 0) return;

        this.idleHandle = scheduleIdle(() => {
            this.idleHandle = null;
            void this.drainOne();
        });
    }

    private async drainOne(): Promise<void> {
        if (this.disposed || this.runningKey !== null) return;

        let next: Job | null = null;
        for (const job of this.pending.values()) {
            if (!next || job.priority < next.priority) next = job;
        }
        if (!next) return;

        this.pending.delete(next.key);
        this.runningKey = next.key;

        try {
            await next.run();
        } catch {
            // Провал одной миниатюры не должен останавливать очередь: страница
            // просто останется плейсхолдером.
        } finally {
            this.runningKey = null;
            this.schedule();
        }
    }
}
